import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { logger } from "@/lib/logger";

const AuthContext = createContext({
  user: null,
  loading: true,
  authModalOpen: false,
  login: () => {},
  loginWithGoogle: () => {},
  openAuthModal: () => {},
  closeAuthModal: () => {},
  logout: () => {},
  refresh: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(r.data);
      return r.data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const openAuthModal = useCallback(() => setAuthModalOpen(true), []);
  const closeAuthModal = useCallback(() => setAuthModalOpen(false), []);

  // `login` keeps its old name but now opens the modal so the user can choose
  // between email/password and Google instead of being redirected directly.
  const login = useCallback(() => setAuthModalOpen(true), []);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/mon-compte";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); }
    catch (e) { logger.warn("Logout request failed", e); }
    // Clear the local "déposer une annonce" draft so the next user on this
    // device never sees the previous user's unfinished ad / images.
    try { localStorage.removeItem("fodindin:draft"); } catch { /* noop */ }
    setUser(null);
    window.location.href = "/";
  }, []);

  const value = useMemo(
    () => ({ user, loading, authModalOpen, login, loginWithGoogle, openAuthModal, closeAuthModal, logout, refresh }),
    [user, loading, authModalOpen, login, loginWithGoogle, openAuthModal, closeAuthModal, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
